/**
 * Convert a SCADA page into the DR CoreUI / NexBus admin panel JSON import format.
 *
 * Output: a top-level JSON array of absolutely-positioned elements on a 1915x789 canvas.
 * Each pump produces: pump body image, status bitlamp, EG/FBS/VS value boxes, ID label.
 * Each loop also produces output cluster (BS green / DM / SS) and KULE label.
 */

import { PUMP_CATALOG, OVERVIEW_LOOPS } from "@/hooks/useSimulation";
import { VERTICAL_GROUPS } from "@/components/scada/PumpRender";
import JSZip from "jszip";

const ts = () => new Date().toISOString();
let _counter = 0;
const newId = () => {
  _counter += 1;
  return `EL_${Date.now()}_${_counter.toString(36)}`;
};
const itemId = () => Math.floor(1000 + Math.random() * 9000);

const isVertical = (id) => VERTICAL_GROUPS.has(PUMP_CATALOG[id].group);

/* ---------- inline SVG pump renderers (return string, used as data URI) ---------- */

const horizontalPumpSvg = (color) => `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 160" width="220" height="160">
  <defs>
    <linearGradient id="motor" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#3a3a3a"/><stop offset="45%" stop-color="#1a1a1a"/><stop offset="100%" stop-color="#050505"/>
    </linearGradient>
    <radialGradient id="vol" cx="35%" cy="30%" r="75%">
      <stop offset="0%" stop-color="#fff" stop-opacity="0.55"/>
      <stop offset="35%" stop-color="${color}"/>
      <stop offset="100%" stop-color="#000" stop-opacity="0.85"/>
    </radialGradient>
  </defs>
  <g transform="translate(0,5)">
    <rect x="20" y="124" width="180" height="12" fill="#1a1a1a" rx="2"/>
    <rect x="25" y="136" width="22" height="7" fill="#0a0a0a"/>
    <rect x="173" y="136" width="22" height="7" fill="#0a0a0a"/>
    <ellipse cx="48" cy="70" rx="14" ry="38" fill="#0a0a0a"/>
    <rect x="48" y="32" width="62" height="76" fill="url(#motor)"/>
    <ellipse cx="110" cy="70" rx="14" ry="38" fill="#1a1a1a"/>
    <rect x="64" y="16" width="32" height="18" rx="2" fill="#1a1a1a"/>
    <rect x="110" y="58" width="14" height="24" fill="#5a5a5a"/>
    <ellipse cx="160" cy="70" rx="34" ry="40" fill="url(#vol)" stroke="#0a0a0a"/>
    <circle cx="160" cy="70" r="14" fill="#0a1a14" stroke="#000" stroke-width="1.5"/>
    <g transform="translate(160 70)">
      ${[0, 72, 144, 216, 288].map(a => `<path d="M 0 0 Q 3 -5 0 -10 Q -3 -5 0 0 Z" fill="#7dfba7" stroke="#1fd35e" stroke-width="0.6" transform="rotate(${a})"/>`).join("")}
      <circle r="2.4" fill="#1a3a26"/>
    </g>
    <rect x="150" y="14" width="22" height="22" fill="${color}"/>
    <rect x="150" y="104" width="22" height="22" fill="${color}"/>
    <text x="78" y="74" font-size="6" fill="#fff" opacity="0.45" font-family="Arial" font-weight="bold">GRUNDFOS</text>
  </g>
</svg>`;

const verticalPumpSvg = (color) => `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 230" width="150" height="230">
  <defs>
    <linearGradient id="vMotor" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#0a0a0a"/><stop offset="35%" stop-color="#3a3a3a"/><stop offset="65%" stop-color="#1a1a1a"/><stop offset="100%" stop-color="#000"/>
    </linearGradient>
    <linearGradient id="steel" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#5a6976"/><stop offset="25%" stop-color="#cdd3da"/><stop offset="50%" stop-color="#e8eef3"/><stop offset="75%" stop-color="#b0bcc9"/><stop offset="100%" stop-color="#5a6976"/>
    </linearGradient>
  </defs>
  <g transform="translate(0,4)">
    <rect x="25" y="0" width="100" height="14" rx="3" fill="#1a1a1a"/>
    <rect x="22" y="12" width="106" height="36" fill="url(#vMotor)"/>
    <rect x="32" y="16" width="28" height="22" rx="2" fill="#0a0a0a"/>
    <rect x="63" y="16" width="22" height="22" rx="2" fill="#0a0a0a"/>
    <rect x="88" y="16" width="30" height="22" rx="2" fill="#0a0a0a"/>
    <path d="M 25 48 L 125 48 L 110 75 L 40 75 Z" fill="${color}"/>
    <rect x="38" y="73" width="74" height="6" fill="#1a1a1a"/>
    <rect x="40" y="83" width="22" height="95" fill="url(#steel)"/>
    <rect x="64" y="83" width="22" height="95" fill="url(#steel)"/>
    <rect x="88" y="83" width="22" height="95" fill="url(#steel)"/>
    <circle cx="75" cy="130" r="14" fill="#06120c" stroke="#000" stroke-width="1.5"/>
    <g transform="translate(75 130)">
      ${[0, 72, 144, 216, 288].map(a => `<path d="M 0 0 Q 3 -5 0 -10 Q -3 -5 0 0 Z" fill="#7dfba7" stroke="#1fd35e" stroke-width="0.5" transform="rotate(${a})"/>`).join("")}
      <circle r="2.4" fill="#1a3a26"/>
    </g>
    <rect x="22" y="183" width="106" height="32" fill="#1a1a1a"/>
    <polygon points="0,192 18,190 22,196 22,210 18,216 0,214" fill="#1a1a1a"/>
    <polygon points="150,192 132,190 128,196 128,210 132,216 150,214" fill="#1a1a1a"/>
    <text x="75" y="34" text-anchor="middle" font-size="5" fill="#fff" opacity="0.45" font-family="Arial" font-weight="bold">GRUNDFOS</text>
  </g>
</svg>`;

const dataUriSvg = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

/* ---------- element factory ---------- */

const baseEl = () => ({
  TimeStamp: ts(),
  Title: "",
  TagId: null,
  TagName: null,
  DisplayName: "",
  Value: 0,
  Birim: "",
  RecordType: "instant",
  AlertCode: 0,
  background: "transparent",
  "background-image": "",
  "background-repeat": null,
  ItemID: itemId(),
  "font-size": "13px",
  "font-weight": "normal",
  "font-style": "normal",
  "font-family": "Arial",
  color: "#ffffff",
  "border-color": "transparent",
  "border-width": "0px",
  "padding-top": null,
  "text-align": "center",
  "text-allign": "center",
  cursor: "",
  elementId: newId(),
  staticText: "",
  "background-size": "initial",
  "z-index": 5,
  navigate: "",
  classList: "context-menu-one",
  popup: "nopopup",
  PopOverTitle: "",
  PopOverSuffixes: {},
  PopOverLabels: {},
  precision: 1,
  Lock: false,
  Exp: "",
  PopOver: false,
  PopOverEvent: null,
  UserRole: 0,
});

const px = (n) => `${Math.round(n)}px`;

/* ---------- builders ---------- */

const bg = () => ({
  ...baseEl(),
  elementType: "div",
  left: "0px", top: "0px", width: "1915px", height: "789px",
  background: "#f4f6f8",
  "z-index": 0,
  DisplayName: "Arka Plan",
  Lock: true,
});

const title = (text, subtitle) => [
  {
    ...baseEl(),
    elementType: "div",
    left: "40px", top: "20px", width: "900px", height: "32px",
    "font-size": "22px", "font-weight": "bold", color: "#0a1118",
    "text-align": "left", "text-allign": "left",
    staticText: text,
    DisplayName: "Sayfa Başlığı",
    "z-index": 2,
  },
  {
    ...baseEl(),
    elementType: "div",
    left: "40px", top: "54px", width: "900px", height: "20px",
    "font-size": "12px", color: "#5a6976",
    "text-align": "left", "text-allign": "left",
    staticText: subtitle,
    "z-index": 2,
  },
];

const labelValue = (left, top, width, label, tag, unit, precision = 2, green = false) => [
  // Label text
  {
    ...baseEl(),
    elementType: "div",
    left: px(left), top: px(top), width: "34px", height: "24px",
    "font-size": "13px", "font-weight": "bold", color: "#1a1a1a",
    "text-align": "left", "text-allign": "left",
    staticText: label,
    "z-index": 6,
  },
  // Value box (with TagName for live data)
  {
    ...baseEl(),
    elementType: "div",
    left: px(left + 34), top: px(top + 2), width: px(width - 34), height: "20px",
    background: green ? "#a8e89a" : "#ffffff",
    "border-color": "#2563a0", "border-width": "1px",
    "font-size": "11px", color: "#0a1118",
    "text-align": "center", "text-allign": "center",
    staticText: "",
    TagName: tag,
    Birim: unit,
    precision: precision,
    "z-index": 7,
    DisplayName: `${label} ${unit}`,
  },
];

const pipeH = (left, top, width) => ({
  ...baseEl(),
  elementType: "div",
  left: px(left), top: px(top), width: px(width), height: "6px",
  background: "#2563a0",
  "z-index": 1,
});

const pipeV = (left, top, height) => ({
  ...baseEl(),
  elementType: "div",
  left: px(left), top: px(top), width: "6px", height: px(height),
  background: "#2563a0",
  "z-index": 1,
});

const pumpImage = (left, top, id) => {
  const def = PUMP_CATALOG[id];
  const v = isVertical(id);
  const w = v ? 150 : 220;
  const h = v ? 230 : 160;
  const svg = v ? verticalPumpSvg(def.color) : horizontalPumpSvg(def.color);
  return {
    ...baseEl(),
    elementType: "img",
    left: px(left), top: px(top), width: px(w), height: px(h),
    background: "transparent",
    "background-image": `url("${dataUriSvg(svg)}")`,
    "background-size": "contain",
    "background-repeat": "no-repeat",
    staticText: "",
    DisplayName: `${def.group} ${id}`,
    "z-index": 5,
    cursor: "pointer",
  };
};

const pumpStatusLamp = (left, top, id) => ({
  ...baseEl(),
  elementType: "bitlamp",
  left: px(left), top: px(top), width: "16px", height: "16px",
  TagName: `Pump_${id.replace("-", "_")}_Run`,
  bitlampLabel: "",
  bitlampColorOn: "#4CAF50",
  bitlampColorOff: "#9E9E9E",
  bitlampShape: "circle",
  DisplayName: `${id} Çalışma Lambası`,
  "z-index": 8,
});

const pumpIdLabel = (left, top, w, id) => ({
  ...baseEl(),
  elementType: "div",
  left: px(left), top: px(top), width: px(w), height: "20px",
  background: "#ffffff",
  "border-color": "#2563a0", "border-width": "1px",
  "font-size": "12px", "font-weight": "bold", color: "#0a3a5a",
  "text-align": "center", "text-allign": "center",
  staticText: id,
  "z-index": 7,
});

const kuleLabel = (left, top, text) => ({
  ...baseEl(),
  elementType: "div",
  left: px(left), top: px(top), width: "180px", height: "60px",
  "font-size": "44px", "font-weight": "bold", color: "#0a1118",
  "text-align": "left", "text-allign": "left",
  staticText: text,
  DisplayName: text,
  "z-index": 4,
});

/* ---------- per-pump block (label group + pump + pipes) ---------- */

const buildPumpBlock = (startLeft, baseTop, id) => {
  const els = [];
  const v = isVertical(id);
  const pumpW = v ? 150 : 220;
  const pumpH = v ? 230 : 160;
  const tagName = `Pump_${id.replace("-", "_")}`;

  // EG + FBS row at top
  const labelsTop = baseTop;
  els.push(...labelValue(startLeft, labelsTop, 100, "EG", `${tagName}_Power`, "kW", 2));
  els.push(...labelValue(startLeft + 110, labelsTop, 100, "FBS", `${tagName}_Pressure`, "Bar", 2));

  // Horizontal pipe across labels
  els.push(pipeH(startLeft, labelsTop + 28, 220));
  // Drop pipe down to pump
  const pumpLeft = startLeft + (220 - pumpW) / 2;
  const pumpTop = labelsTop + 50;
  els.push(pipeV(startLeft + 110, labelsTop + 34, 18));

  // Pump image
  els.push(pumpImage(pumpLeft, pumpTop, id));
  // Status lamp on top-right of pump
  els.push(pumpStatusLamp(pumpLeft + pumpW - 22, pumpTop + 6, id));
  // Pump ID label
  els.push(pumpIdLabel(pumpLeft + (pumpW - 60) / 2, pumpTop + pumpH - 10, 60, id));

  // Bottom pipes
  els.push(pipeV(startLeft + 110, pumpTop + pumpH + 4, 18));
  els.push(pipeH(startLeft, pumpTop + pumpH + 22, 220));

  // VS bottom value
  els.push(...labelValue(startLeft + 55, pumpTop + pumpH + 32, 110, "VS", `${tagName}_Vacuum`, "Pa", 0));

  return { els, blockWidth: 220, blockBottom: pumpTop + pumpH + 56 };
};

/* ---------- output cluster (BS, DM, SS) ---------- */

const outputCluster = (left, top, loopId) => {
  const els = [];
  // arrow + BS
  els.push(...labelValue(left, top, 100, "BS", `Loop${loopId}_Pressure`, "Bar", 2, true));
  els.push(pipeH(left + 100, top + 12, 16));
  els.push(...labelValue(left + 116, top, 110, "DM", `Loop${loopId}_Flow`, "m³/h", 1));
  els.push(pipeH(left + 226, top + 12, 16));
  els.push(...labelValue(left + 242, top, 100, "SS", `Loop${loopId}_TempOut`, "°C", 1));
  els.push(pipeH(left + 342, top + 12, 30));

  // Return line below
  els.push(pipeH(left + 50, top + 50, 180));
  els.push(...labelValue(left + 230, top + 38, 110, "SS", `Loop${loopId}_TempReturn`, "°C", 1));

  return els;
};

/* ---------- main exporter (per route) ---------- */

export function exportPageToScada(route) {
  const arr = [];
  arr.push(bg());

  if (route === "/") {
    arr.push(...title("BRISA SCADA · Genel Bakış", "İki soğutma kulesi · 8 pompa"));
    const loops = [
      { y: 90, loop: OVERVIEW_LOOPS.loop1, id: 1, label: "KULE-1" },
      { y: 410, loop: OVERVIEW_LOOPS.loop2, id: 2, label: "KULE-2" },
    ];
    loops.forEach(({ y, loop, id, label }) => {
      let cur = 40;
      loop.ids.forEach((pid) => {
        const { els } = buildPumpBlock(cur, y, pid);
        arr.push(...els);
        cur += 230;
      });
      // Right-side output cluster + KULE label
      const clusterLeft = cur + 20;
      arr.push(...outputCluster(clusterLeft, y + 50, id));
      arr.push(kuleLabel(clusterLeft + 380, y + 30, label));
    });
  } else {
    const routeMap = {
      "/kazan": { title: "Kazan Pompaları", subtitle: "3 pompa · Yüksek basınç", ids: ["K-1", "K-2", "K-3"] },
      "/degazor": { title: "Degazör Pompaları", subtitle: "2 pompa", ids: ["D-1", "D-2"] },
      "/isg": { title: "İSG Revir / Isıtma", subtitle: "1 pompa", ids: ["I-1"] },
      "/hidrofor": { title: "Kullanım Suyu Hidrofor", subtitle: "1 pompa", ids: ["H-1"] },
      "/filtreleme": { title: "Filtreleme Pompaları", subtitle: "2 pompa", ids: ["F-1", "F-2"] },
    };
    const cfg = routeMap[route] || routeMap["/kazan"];
    arr.push(...title(`BRISA SCADA · ${cfg.title}`, cfg.subtitle));

    let cur = 60;
    cfg.ids.forEach((pid, idx) => {
      // Pump number badge
      arr.push({
        ...baseEl(),
        elementType: "div",
        left: px(cur + 95), top: "80px", width: "30px", height: "30px",
        "font-size": "22px", "font-weight": "bold", color: "#c0c4cc",
        "text-align": "center", "text-allign": "center",
        staticText: String(idx + 1),
        "z-index": 3,
      });
      const { els } = buildPumpBlock(cur, 120, pid);
      arr.push(...els);
      cur += 260;
    });

    // Aggregate cluster on right
    const clusterLeft = cur + 20;
    arr.push(...outputCluster(clusterLeft, 180, cfg.title.split(" ")[0]));

    // Summary panel
    arr.push({
      ...baseEl(),
      elementType: "div",
      left: px(clusterLeft), top: "280px", width: "330px", height: "110px",
      background: "#f1f4f8",
      "border-color": "#c0c4cc", "border-width": "1px",
      "font-size": "11px", color: "#5a6976",
      "text-align": "left", "text-allign": "left",
      staticText: "Grup Özeti — TagName'ler ile canlı veriler bağlanabilir.",
      "padding-top": "8px",
      "z-index": 3,
    });
  }

  // Footer info
  arr.push({
    ...baseEl(),
    elementType: "div",
    left: "0px", top: "760px", width: "1915px", height: "20px",
    background: "#ffffff",
    "border-color": "#c0c4cc", "border-width": "1px",
    "font-size": "10px", color: "#5a6976",
    "text-align": "center", "text-allign": "center",
    staticText: "● PLC ONLINE  ·  MODBUS TCP 192.168.1.10  ·  © BRISA SCADA Demo",
    "z-index": 2,
  });

  return arr;
}

/* ---------- download helper ---------- */

export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------- BULK pump image download (ZIP) ---------- */

// Color → group mapping (unique pumps to export)
const UNIQUE_PUMPS = [
  { name: "pump-kazan-vertical",        color: "#cf2027", orientation: "vertical",   group: "Kazan" },
  { name: "pump-isg-vertical",          color: "#7a3dcf", orientation: "vertical",   group: "İSG / Isıtma" },
  { name: "pump-hidrofor-vertical",     color: "#1faf6b", orientation: "vertical",   group: "Hidrofor" },
  { name: "pump-degazor-horizontal",    color: "#e85a1a", orientation: "horizontal", group: "Degazör" },
  { name: "pump-filtreleme-horizontal", color: "#1f6bcf", orientation: "horizontal", group: "Filtreleme" },
];

// Convert SVG string → PNG data URL via offscreen canvas (returns a Promise<Blob>)
function svgToPngBlob(svgString, width, height, scale = 2) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error("png blob null"));
      }, "image/png");
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function downloadAllPumpImages() {
  const zip = new JSZip();
  const svgFolder = zip.folder("svg");
  const pngFolder = zip.folder("png");

  for (const p of UNIQUE_PUMPS) {
    const svg = p.orientation === "vertical" ? verticalPumpSvg(p.color) : horizontalPumpSvg(p.color);
    const w = p.orientation === "vertical" ? 150 : 220;
    const h = p.orientation === "vertical" ? 230 : 160;
    svgFolder.file(`${p.name}.svg`, svg);
    try {
      const pngBlob = await svgToPngBlob(svg, w, h, 3); // 3x scale for crisp PNG
      pngFolder.file(`${p.name}.png`, pngBlob);
    } catch (e) {
      console.warn("PNG conversion failed for", p.name, e);
    }
  }

  // README
  const readme = `BRISA SCADA — Pompa Görsel Paketi
===================================

İki klasör halinde tüm benzersiz pompa görselleri:

/svg  → Vektörel SVG (ölçeklenir, küçük dosya)
/png  → 3× yüksek çözünürlük PNG (transparan zemin)

Dosya adlandırma: pump-<grup>-<yön>.{svg,png}

Grup → renk eşleştirmesi:
${UNIQUE_PUMPS.map(p => `  ${p.name.padEnd(36)}  ${p.group.padEnd(18)}  ${p.color}`).join("\n")}

DR CoreUI / NexBus admin panele yükleme:
1. Admin Panel → Uploads sekmesi → bu görselleri yükleyin
2. Import JSON'da img elementlerinin background-image alanını
   "url(/uploads/pump-kazan-vertical.png)" şeklinde güncelleyebilirsiniz
   (alternatif olarak mevcut JSON'da data: URI gömülü olduğu için doğrudan da çalışır)
`;
  zip.file("README.txt", readme);

  const content = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = "brisa-scada-pump-images.zip";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
